/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { ViewModel } from "../../../Common/ViewModel";
import { Panel } from "../../../../../ui/components/Panel";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { BEM, TTranslateHookFn, useTranslate } from "@adapptio/lib-components";
import { useTreeState } from "../../../../../ui/components/Tree/useTreeState";
import { ITreeViewProps, TreeView } from "../../../../../ui/components/Tree/TreeView";
import {
	ITreeNode,
	ITreeNodeAction,
	TREE_NODE_ACTION_STYLE,
	TREE_VIEW_STYLE
} from "../../../../../ui/components/Tree/TreeViewTypes";
import {
	DataSourceAction,
	DataSourceConstant,
	DataSourceContextConsumer,
	DataSourceContextProvider,
	DataSourceMethod,
	DataSourceTimer,
	DataSourceVariable,
	DataSourceWebSocketClient,
	TBlueprintViewSchema
} from "@adapptio/lib-core";
import {
	IModelQueryTreeResultItem,
	ISchemaBuilderAny,
	ISchemaDataSourceModel,
	isModel,
	isSchemaComponentListModel,
	isSchemaComponentModel,
	isSchemaConstArrayModel,
	isSchemaConstMapModel,
	isSchemaConstMapModelEntry,
	isSchemaConstStringModel,
	isSchemaDataSourceModel,
	isSchemaExpressionModel,
	isSchemaValueModel,
	QUERY_FN_OP,
	queryModelTree,
	TGenericModelNode,
	TGetBlueprintSchemaModel
} from "@adapptio/lib-blueprint";
import { DROPDOWN_STYLE, IDropdownMenuItem } from "../../../../../ui/components/DropdownMenu";
import { ViewEditSelection } from "../../../Common/ViewEditSelection";
import { createTreeNodesFromMapModel } from "../../../../../ui/components/Tree/utils";
import { useContextMenu } from "../../../../../ui/hooks/useContextMenu";
import { useEditorMainContext } from "../../../../../ui/contexts/EditorMainContext";

function createAddDataSourceMenu(
	model: TGetBlueprintSchemaModel<TBlueprintViewSchema>,
	selection: ViewEditSelection
): IDropdownMenuItem[] {
	const viewProps = model.props.spec.value.props;
	const dataSources = viewProps.dataSources.value;

	const addDataSource = (type: string) => {
		const ds = dataSources.schema.addItem(
			dataSources,
			dataSources.items.length,
			{
				id: model.ctx.getUniqueIdentifier(type, true),
				type: type,
				opts: {}
			},
			true
		);

		selection.selectDataSource(ds);
	};

	return [
		{
			id: "action",
			label: "Add Data from Action",
			icon: DataSourceAction.icon,
			onClick: () => addDataSource(DataSourceAction.name)
		},
		{
			id: "variable",
			label: "Add State Variable",
			icon: DataSourceVariable.icon,
			onClick: () => addDataSource(DataSourceVariable.name)
		},
		{
			id: "constant",
			label: "Add Constant",
			icon: DataSourceConstant.icon,
			onClick: () => addDataSource(DataSourceConstant.name)
		},
		{
			id: "method",
			label: "Add Method",
			icon: DataSourceMethod.icon,
			onClick: () => addDataSource(DataSourceMethod.name)
		},
		{
			id: "contextProvider",
			label: "Add Context Provider",
			icon: DataSourceContextProvider.icon,
			onClick: () => addDataSource(DataSourceContextProvider.name)
		},
		{
			id: "contextConsumer",
			label: "Add Context Consumer",
			icon: DataSourceContextConsumer.icon,
			onClick: () => addDataSource(DataSourceContextConsumer.name)
		},
		{
			id: "webSocketClient",
			label: "Add WebSocket Client",
			icon: DataSourceWebSocketClient.icon,
			onClick: () => addDataSource(DataSourceWebSocketClient.name)
		},
		{
			id: "timer",
			label: "Add Timer",
			icon: DataSourceTimer.icon,
			onClick: () => addDataSource(DataSourceTimer.name)
		},
	];
}

interface IGetTreeOpts {
	actions?: ITreeNodeAction[];
}

function getDataSourceTree(
	model: TGetBlueprintSchemaModel<TBlueprintViewSchema>,
	{ actions = [] }: IGetTreeOpts = {}
): ITreeNode[] {
	const viewProps = model.props.spec.value.props;
	const dataSources = viewProps.dataSources.value;

	const items = dataSources.items.map((dsItem) => {
		let icon: string;
		let description: string;

		switch (dsItem.dsType) {
			case "action": {
				icon = DataSourceAction.icon;
				description = "Action";
				break;
			}
			case "variable": {
				icon = DataSourceVariable.icon;
				description = "Variable";
				break;
			}
			case "constant": {
				icon = DataSourceConstant.icon;
				description = "Constant";
				break;
			}
			case "method": {
				icon = DataSourceMethod.icon;
				description = "Method";
				break;
			}
			case "contextProvider": {
				icon = DataSourceContextProvider.icon;
				description = "Ctx. Provider";
				break;
			}
			case "contextConsumer": {
				icon = DataSourceContextConsumer.icon;
				description = "Ctx. Consumer";
				break;
			}
			case "webSocketClient": {
				icon = DataSourceWebSocketClient.icon;
				description = "WebSocket Client";
				break;
			}
			case "timer": {
				icon = DataSourceTimer.icon;
				description = "Timer";
				break;
			}
			case "parameter": {
				icon = "mdi/code-braces";
				description = "Parameter";
				break;
			}
			default: {
				icon = "mdi/help";
				description = "Unknown";
			}
		}

		return {
			id: String(dsItem.nodeId),
			label: dsItem.id.value,
			icon: icon,
			description: description,
			selectable: true,
			metaData: {
				modelNode: dsItem,
				primaryListModel: dataSources
			}
		} as ITreeNode;
	});

	const rootClass = BEM("edt-module-view-editor-outline__tree-node-section", null, {
		"is-empty": items.length === 0
	});

	return [
		{
			id: "root",
			label: "Data",
			icon: "mdi/database-plus",
			defaultExpanded: true,
			children: items.length > 0 ? items : [],
			className: rootClass.toClassName(),
			actions
		}
	];
}

const subjectTypes = new Set([
	"constObject",
	"constArray",
	"constMap",
	"value",
	"scopedTemplate",
	"conditional",
	"namedConfig",
	"oneOf",
	"optGroup",
	"override",
	"recursive",
	"responsiveValue",
	"runtimeWrapper"
]);

function getComponentTree(
	t: TTranslateHookFn,
	model: TGetBlueprintSchemaModel<TBlueprintViewSchema>,
	{ getRouteLabel }: { getRouteLabel: (routeName: string) => string }
): ITreeNode[] {
	const viewProps = model.props.spec.value.props;

	const queryResult = queryModelTree(viewProps.content, (item) => {
		// Allow traversal only on models that can contain queried subjects
		if (subjectTypes.has(item.node.schema.name)) {
			return {
				match: false,
				op: QUERY_FN_OP.CONTINUE
			};
		}

		const routesPath = item.path.some(
			(pathItem) =>
				isSchemaComponentModel(pathItem.node) && pathItem.node.componentHandler.name === "routes"
		);

		if (routesPath) {
			const routeNameItem = item.path.find((pathItem) => pathItem.key === "props.routeName");

			if (routeNameItem) {
				const itemMatch =
					isSchemaValueModel(routeNameItem.node) && routeNameItem.node.type === "expression"
						? isSchemaExpressionModel(item.node)
						: !isSchemaExpressionModel(item.node);

				return {
					match: itemMatch,
					op: QUERY_FN_OP.STOP
				};
			}
		}

		const oneOfPath = item.path.some(
			(pathItem) =>
				isSchemaComponentModel(pathItem.node) && pathItem.node.componentHandler.name === "oneOf"
		);

		if (oneOfPath) {
			const optionName = item.path.find((pathItem) => pathItem.key === "props.name");

			if (optionName) {
				const itemMatch =
					isSchemaValueModel(optionName.node) && optionName.node.type === "expression"
						? isSchemaExpressionModel(item.node)
						: !isSchemaExpressionModel(item.node);

				return {
					match: itemMatch,
					op: QUERY_FN_OP.STOP
				};
			}
		}

		// Include component, component lists and component events
		if (isSchemaComponentModel(item.node) || isSchemaComponentListModel(item.node)) {
			return {
				match: true,
				op: QUERY_FN_OP.CONTINUE
			};
		}

		// Otherwise stop
		return {
			match: false,
			op: QUERY_FN_OP.STOP
		};
	});

	const getTreeNode = (queryItem: IModelQueryTreeResultItem): ITreeNode => {
		const node = queryItem.node;

		if (isSchemaComponentModel(node)) {
			const children: ITreeNode[] = [];

			const routesNode = node.componentHandler.name === "routes";
			const oneOfNode = node.componentHandler.name === "oneOf";

			if (routesNode || oneOfNode) {
				const itemIcon = {
					routes: "mdi/sign-direction",
					oneOf: "mdi/rhombus-medium-outline"
				}[node.componentHandler.name];

				const childItems: ITreeNode[] = [];

				// children are in pairs like this [nameModel1, contentModel1]
				for (let i = 0; i < queryItem.children.length; i += 2) {
					const nameNode = queryItem.children[i].node;
					const contentNode = queryItem.children[i + 1].node;

					if (
						!(isSchemaConstStringModel(nameNode) || isSchemaExpressionModel(nameNode)) ||
						!isSchemaComponentListModel(contentNode)
					) {
						continue;
					}

					let listModel = null;
					let modelNode: TGenericModelNode = nameNode;

					while (isModel(modelNode) && !listModel) {
						if (isSchemaConstArrayModel(modelNode.parent)) {
							listModel = modelNode.parent;
							break;
						}

						modelNode = modelNode.parent;
					}

					const itemLabel = routesNode ? getRouteLabel(nameNode.value) : nameNode.value;

					childItems.push({
						id: String(modelNode.nodeId),
						label: itemLabel,
						icon: itemIcon,
						children: queryItem.children[i + 1].children.map((child) => getTreeNode(child)),
						isLoadable: false,
						selectable: true,
						metaData: {
							modelNode: modelNode,
							primaryListModel: listModel,
							contentNode: contentNode
						}
					});
				}

				children.push(...childItems);
			} else {
				const childItems =
					queryItem.children.length === 1
						? queryItem.children[0].children.map((child) => getTreeNode(child))
						: queryItem.children.map((child) => getTreeNode(child));
				children.push(...childItems);
			}

			const cleanChildren = children.filter((child) => !!child);

			return {
				id: String(node.nodeId),
				label: node.id.value,
				icon: node.componentHandler.icon,
				children: cleanChildren.length > 0 ? cleanChildren : null,
				isLoadable: cleanChildren.length > 0,
				selectable: true,
				isLeaf: cleanChildren.length === 0,
				metaData: {
					modelNode: node,
					primaryListModel: queryItem.children[0]?.node
				}
			};
		}

		if (isSchemaComponentListModel(node)) {
			const children = queryItem.children.map((child) => getTreeNode(child));
			const nodeInfo = node.schema.getModelNodeInfo(node);

			return {
				id: String(node.nodeId),
				label: t("editor", nodeInfo.label),
				icon: nodeInfo.icon,
				children: children.length > 0 ? children : null,
				isLoadable: children.length > 0,
				selectable: true,
				metaData: {
					modelNode: node,
					primaryListModel: node
				}
			};
		}
	};

	const node = queryResult[0]?.node;
	const items = queryResult[0] ? queryResult[0].children.map((item) => getTreeNode(item)) : [];

	const rootClass = BEM("edt-module-view-editor-outline__tree-node-section", null, {
		"is-empty": items.length === 0
	});

	return [
		{
			id: "root",
			label: "Components",
			icon: "mdi/toy-brick",
			defaultExpanded: true,
			children: items.length > 0 ? items : null,
			className: rootClass.toClassName(),
			metaData: {
				modelNode: node,
				primaryListModel: node
			}
		}
	];
}

function getParametersTree(
	model: TGetBlueprintSchemaModel<TBlueprintViewSchema>,
	{ actions = [] }: IGetTreeOpts = {}
): ITreeNode[] {
	const viewProps = model.props.spec.value.props;
	const paramsModel = viewProps.params;
	const mapModel = paramsModel.props.props.props;

	const items: ITreeNode[] = createTreeNodesFromMapModel(mapModel, { icon: "mdi/record-circle-outline" });

	const rootClass = BEM("edt-module-view-editor-outline__tree-node-section", null, {
		"is-empty": items.length === 0
	});

	return [
		{
			id: "root",
			label: "Parameters",
			icon: "mdi/location-enter",
			defaultExpanded: true,
			children: items.length > 0 ? items : [],
			className: rootClass.toClassName(),
			actions
		}
	];
}

function getOutletsTree(
	model: TGetBlueprintSchemaModel<TBlueprintViewSchema>,
	{ actions = [] }: IGetTreeOpts = {}
): ITreeNode[] {
	const viewProps = model.props.spec.value.props;
	const outletModel = viewProps.outlets;
	const mapModel = outletModel.value.constant;

	const items: ITreeNode[] = createTreeNodesFromMapModel(mapModel, { icon: "mdi/arrow-right-bold" });

	const rootClass = BEM("edt-module-view-editor-outline__tree-node-section", null, {
		"is-empty": items.length === 0
	});

	return [
		{
			id: "root",
			label: "Outlets",
			icon: "mdi/location-exit",
			defaultExpanded: true,
			children: items.length > 0 ? items : [],
			className: rootClass.toClassName(),
			actions
		}
	];
}

/*
 * Content
 */

export interface IViewEditorOutlineContentProps {
	model: ViewModel;
}

export const ViewEditorOutlineContent: FC<IViewEditorOutlineContentProps> = ({ model: viewModel }) => {
	const t = useTranslate();

	const editorMainCtx = useEditorMainContext();

	const [ modelRef, setModelRef ] = useState(() => ({
		model: viewModel.getModel()
	}));

	const { showMenu } = useContextMenu("outlineNode");

	const [ dsTreeState, handleDsTreeStateUpdate ] = useTreeState({ nodes: { root: { expanded: true } } });
	const [ cmpTreeState, handleCmpTreeStateUpdate ] = useTreeState({ nodes: { root: { expanded: true } } });
	const [ paramsTreeState, handleParamsTreeStateUpdate ] = useTreeState({
		nodes: { root: { expanded: true } }
	});
	const [ outletsTreeState, handleOutletsTreeStateUpdate ] = useTreeState({
		nodes: { root: { expanded: true } }
	});
	const [ selection, setSelection ] = useState([]);

	const handleContextMenuClick = useCallback<ITreeViewProps["onNodeContextMenu"]>(
		(treeNode, event) => {
			showMenu(event, { treeNode });
		},
		[ showMenu ]
	);

	const getRouteLabel = useCallback(
		(routeName: string) => {
			const route = routeName ? editorMainCtx.resourceService.getRouteByName(routeName) : null;
			return route?.label || routeName;
		},
		[ editorMainCtx ]
	);

	// DataSources
	const datasourceAddMenuItems = useMemo(() => {
		return createAddDataSourceMenu(modelRef.model, viewModel.getSelection());
	}, [ modelRef?.model, viewModel ]);

	const dsTreeActions = useMemo<ITreeNodeAction[]>(
		() => [
			{
				id: "add-data",
				icon: "mdi/plus-circle-outline",
				tooltip: "Add Datasource",
				style: TREE_NODE_ACTION_STYLE.GHOST,
				menuItems: datasourceAddMenuItems,
				hasPrimaryAction: false,
				hideMenuArrow: true,
				menuStyle: DROPDOWN_STYLE.COMPACT
			}
		],
		[ datasourceAddMenuItems ]
	);

	const dsTreeItems = useMemo(() => {
		return getDataSourceTree(modelRef.model, { actions: dsTreeActions });
	}, [ modelRef, dsTreeActions ]);

	const handleDsSelect = useCallback((node: ITreeNode) => {
		if (node.metaData?.modelNode) {
			viewModel.getSelection().selectDataSource(node.metaData.modelNode as ISchemaDataSourceModel);
		}
	}, []);

	// Components
	const cmpTreeItems = useMemo(() => {
		return getComponentTree(t, modelRef.model, { getRouteLabel });
	}, [ modelRef ]);

	const handleCmpSelect = useCallback((node: ITreeNode) => {
		let modelToSelect = node.metaData?.contentModel || node.metaData?.modelNode;

		while (isModel(modelToSelect)) {
			if (isSchemaComponentModel(modelToSelect)) {
				viewModel.getSelection().selectComponentByModel(modelToSelect);
				return;
			}

			if (isSchemaComponentListModel(modelToSelect)) {
				viewModel.getSelection().selectComponentList(modelToSelect);
				return;
			}

			modelToSelect = modelToSelect.parent;
		}
	}, []);

	const handleCmpHover = useCallback<ITreeViewProps["onNodeHover"]>((node: ITreeNode, { hovered }) => {
		const modelNode = node.metaData?.contentModel || node.metaData?.modelNode;

		if (isSchemaComponentModel(modelNode)) {
			if (hovered) {
				viewModel.getSelection().setHoveredComponent(modelNode);
			} else {
				viewModel.getSelection().unsetHoveredComponent(modelNode);
			}
		}
	}, []);

	// Parameters
	const addParameter = useCallback(() => {
		const model = viewModel.getModel();
		const viewProps = model.props.spec.value.props;
		const paramsModel = viewProps.params;
		const paramsMapModel = paramsModel.props.props.props;

		const parameter = paramsMapModel.schema.addElement(
			paramsMapModel,
			paramsMapModel.schema.getUniqueKey(paramsMapModel, "parameter", true),
			undefined,
			true
		);

		viewModel.getSelection().selectParameter(paramsMapModel, parameter);
	}, [ viewModel ]);

	const paramTreeActions = useMemo<ITreeNodeAction[]>(
		() => [
			{
				id: "add-parameter",
				icon: "mdi/plus-circle-outline",
				tooltip: "Add Parameter",
				style: TREE_NODE_ACTION_STYLE.GHOST,
				hasPrimaryAction: true,
				onClick: addParameter
			}
		],
		[ addParameter ]
	);

	const paramsTreeItems = useMemo(() => {
		return getParametersTree(modelRef.model, { actions: paramTreeActions });
	}, [ modelRef, paramTreeActions ]);

	const handleParamSelect = useCallback((node: ITreeNode) => {
		const mapModel = node.metaData?.mapModel;
		const mapEntry = node.metaData?.mapEntry;

		if (isSchemaConstMapModel<ISchemaBuilderAny>(mapModel) && isSchemaConstMapModelEntry(mapEntry)) {
			viewModel.getSelection().selectParameter(mapModel, mapEntry);
		}
	}, []);

	// Outlets
	const addOutlet = useCallback(() => {
		const model = viewModel.getModel();
		const viewProps = model.props.spec.value.props;
		const outletsModel = viewProps.outlets;
		const outletsMapModel = outletsModel.value.constant;

		const outlet = outletsMapModel.schema.addElement(
			outletsMapModel,
			outletsMapModel.schema.getUniqueKey(outletsMapModel, "outlet", true),
			undefined,
			true
		);

		viewModel.getSelection().selectOutlet(outletsMapModel, outlet);
	}, [ viewModel ]);

	const outletsTreeActions = useMemo<ITreeNodeAction[]>(
		() => [
			{
				id: "add-outlet",
				icon: "mdi/plus-circle-outline",
				tooltip: "Add Outlet",
				style: TREE_NODE_ACTION_STYLE.GHOST,
				hasPrimaryAction: true,
				onClick: addOutlet
			}
		],
		[ addOutlet ]
	);

	const outletsTreeItems = useMemo(() => {
		return getOutletsTree(modelRef.model, { actions: outletsTreeActions });
	}, [ modelRef, outletsTreeActions ]);

	const handleOutletSelect = useCallback((node: ITreeNode) => {
		const mapModel = node.metaData?.mapModel;
		const mapEntry = node.metaData?.mapEntry;

		if (isSchemaConstMapModel<ISchemaBuilderAny>(mapModel) && isSchemaConstMapModelEntry(mapEntry)) {
			viewModel.getSelection().selectOutlet(mapModel, mapEntry);
		}
	}, []);

	const baseClass = BEM("edt-module-view-editor-outline");

	const handleNodeDragDrop = useCallback<ITreeViewProps["onNodeDragDrop"]>(
		({
			draggedNode,
			dropTargetNode,
			dropPosition
		}) => {
			const itemModel = draggedNode.metaData?.modelNode;
			const targetModel = dropTargetNode.metaData?.modelNode;
			const mapModel = draggedNode.metaData?.mapModel;
			const mapEntry = draggedNode.metaData?.mapEntry;
			const targetEntry = dropTargetNode.metaData?.mapEntry;

			// Handle dragging routes/oneOf children (where contentNode contains the actual component list model)
			// In this case, itemModel is the name string model, not the component
			if (
				draggedNode.metaData?.contentNode &&
				isModel(itemModel) &&
				isSchemaConstArrayModel(itemModel.parent)
			) {
				const arrayModel = itemModel.parent;

				const currentIndex = arrayModel.items.findIndex(
					(item) => item.nodeId === itemModel.nodeId
				);

				let targetNodeIndex: number | null = null;

				// Check if dropping on another route/oneOf child (which also has contentNode)
				if (
					dropTargetNode.metaData?.contentNode &&
					isModel(targetModel) &&
					targetModel.parent === arrayModel
				) {
					targetNodeIndex = arrayModel.items.findIndex((item) => item.nodeId === targetModel.nodeId);
				}

				// Compensate for removal if moving upwards	
				const newIndex = targetNodeIndex !== null
					? (targetNodeIndex > currentIndex ? targetNodeIndex : targetNodeIndex + 1)
					: 0;

				arrayModel.schema.moveElement(
					arrayModel,
					currentIndex,
					newIndex,
					true
				);

				return;
			}

			if (
				isModel(itemModel) &&
				isSchemaConstArrayModel(itemModel.parent)
			) {
				const arrayModel = itemModel.parent;

				const currentIndex = arrayModel.items.findIndex(
					(item) => item.nodeId === itemModel.nodeId
				);

				const targetNodeIndex = targetModel && isModel(targetModel) && targetModel.parent === arrayModel
					? arrayModel.items.findIndex((item) => item.nodeId === targetModel.nodeId)
					: null;

				// Compensate for removal if moving upwards	
				const newIndex = targetNodeIndex !== null
					? (targetNodeIndex > currentIndex ? targetNodeIndex : targetNodeIndex + 1)
					: 0;

				arrayModel.schema.moveElement(
					arrayModel,
					currentIndex,
					newIndex,
					true
				);

				return;
			}

			if (
				isSchemaDataSourceModel(itemModel) &&
				itemModel.parentDataSourceList
			) {
				const listModel = itemModel.parentDataSourceList;

				const currentIndex = listModel.items.findIndex(
					(item) => item === itemModel
				);

				const targetNodeIndex = targetModel && isSchemaDataSourceModel(targetModel)
					? listModel.items.findIndex((item) => item === targetModel)
					: null;

				// Compensate for removal if moving upwards	
				const newIndex = targetNodeIndex !== null
					? (targetNodeIndex > currentIndex ? targetNodeIndex : targetNodeIndex + 1)
					: 0;

				listModel.schema.moveItemByModel(
					listModel,
					itemModel,
					newIndex,
					true
				);

				return;
			}

			if (
				isSchemaComponentModel(itemModel)
			) {
				const targetModel = dropTargetNode?.metaData?.modelNode;
				const primaryListModel = dropTargetNode?.metaData?.primaryListModel;
				const contentNode = dropTargetNode?.metaData?.contentNode;

				// If dropPosition > 0 -> we are dropping after target node
				// If dropPosition === 0 -> we are dropping before or into target node (if container)

				// Dropping into a route/oneOf node (contentNode contains the component list for that route)
				if (dropPosition === 0 && contentNode && isSchemaComponentListModel(contentNode)) {
					itemModel.schema.removeSelfFromComponentList(itemModel, true, false);
					contentNode.schema.addItemModel(contentNode, 0, itemModel, true);
					return;
				}

				// Dropping into component list
				if (dropPosition === 0 && primaryListModel && isSchemaComponentListModel(primaryListModel)) {
					itemModel.schema.removeSelfFromComponentList(itemModel, true, false);
					primaryListModel.schema.addItemModel(primaryListModel, 0, itemModel, true);
					return;
				}

				// Dropping before/after component in the same component list - use move
				if (
					isSchemaComponentModel(targetModel) && itemModel.parentComponentList === targetModel.parentComponentList
				) {
					const parentComponentList = itemModel.parentComponentList;

					const currentIndex = parentComponentList.items.findIndex(
						(item) => item === itemModel
					);

					const targetNodeIndex = targetModel
						? parentComponentList.items.findIndex((item) => item === targetModel)
						: null;

					// Compensate for removal if moving upwards	
					const newIndex = targetNodeIndex !== null
						? (targetNodeIndex > currentIndex ? targetNodeIndex : targetNodeIndex + 1)
						: 0;

					parentComponentList.schema.moveItemByModel(
						itemModel.parentComponentList,
						itemModel,
						newIndex,
						true
					);

					return;
				}

				// Dropping before/after component into different component list
				if (isSchemaComponentModel(targetModel)) {
					const parentComponentList = targetModel.parentComponentList;

					const targetNodeIndex = parentComponentList.items.findIndex(
						(item) => item === targetModel
					);

					if (targetNodeIndex !== null) {
						itemModel.schema.removeSelfFromComponentList(itemModel, true, false);	
						parentComponentList.schema.addItemModel(parentComponentList, targetNodeIndex + 1, itemModel, true);
					}
				}

				return;
			}

			if (
				isSchemaConstMapModel(mapModel) &&
				isSchemaConstMapModelEntry(mapEntry)
			) {
				const currentIndex = mapModel.items.findIndex(
					(item) => item === mapEntry
				);

				const targetNodeIndex = targetEntry && isSchemaConstMapModelEntry(targetEntry)
					? mapModel.items.findIndex((item) => item === targetEntry)
					: null;

				// Compensate for removal if moving upwards	
				const newIndex = targetNodeIndex !== null
					? (targetNodeIndex > currentIndex ? targetNodeIndex : targetNodeIndex + 1)
					: 0;

				mapModel.schema.moveElement(
					mapModel,
					currentIndex,
					newIndex,
					true
				);

				return;
			}
		},
		[ cmpTreeItems, dsTreeItems ]
	);

	useEffect(() => {
		const handleUpdate = () =>
			setModelRef({
				model: viewModel.getModel()
			});

		const handleSelectionChange = () =>
			setSelection(
				viewModel
					.getSelection()
					.getItems()
					.map((item) => String(item.nodeId))
			);

		onEvent(viewModel.onModelChange, handleUpdate);
		onEvent(viewModel.getSelection().onChange, handleSelectionChange);

		return () => {
			offEvent(viewModel.onModelChange, handleUpdate);
			offEvent(viewModel.getSelection().onChange, handleSelectionChange);
		};
	}, [ viewModel ]);

	return (
		<Panel scroll={true} className={baseClass.toClassName()}>
			<div className={baseClass.element("data-sources").toClassName()}>
				<TreeView
					items={dsTreeItems}
					state={dsTreeState}
					selections={selection}
					onNodeSelect={handleDsSelect}
					onStateUpdate={handleDsTreeStateUpdate}
					onNodeContextMenu={handleContextMenuClick}
					isDraggable
					style={TREE_VIEW_STYLE.DEFAULT}
					onNodeDragDrop={handleNodeDragDrop}
					// sort
				/>
			</div>
			<div className={baseClass.element("components").toClassName()}>
				<TreeView
					items={cmpTreeItems}
					state={cmpTreeState}
					selections={selection}
					onNodeSelect={handleCmpSelect}
					onNodeHover={handleCmpHover}
					onStateUpdate={handleCmpTreeStateUpdate}
					onNodeContextMenu={handleContextMenuClick}
					style={TREE_VIEW_STYLE.DEFAULT}
					isDraggable
					onNodeDragDrop={handleNodeDragDrop}
				/>
				{!cmpTreeItems[0].children ? (
					<div className={baseClass.element("note").toClassName()}>
						Add components by dragging them from the right panel.
					</div>
				) : null}
			</div>
			<div className={baseClass.element("parameters").toClassName()}>
				<TreeView
					items={paramsTreeItems}
					state={paramsTreeState}
					selections={selection}
					onNodeSelect={handleParamSelect}
					onStateUpdate={handleParamsTreeStateUpdate}
					onNodeContextMenu={handleContextMenuClick}
					style={TREE_VIEW_STYLE.DEFAULT}
					isDraggable
					onNodeDragDrop={handleNodeDragDrop}
				/>
			</div>
			<div className={baseClass.element("outlets").toClassName()}>
				<TreeView
					items={outletsTreeItems}
					state={outletsTreeState}
					selections={selection}
					onNodeSelect={handleOutletSelect}
					onStateUpdate={handleOutletsTreeStateUpdate}
					onNodeContextMenu={handleContextMenuClick}
					style={TREE_VIEW_STYLE.DEFAULT}
					isDraggable
					onNodeDragDrop={handleNodeDragDrop}
				/>
			</div>
		</Panel>
	);
};
